import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, ArrowLeft, CheckCircle2, Link2 } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ManualOverheadEditor, type ManualOverheadDraft } from "../components/ManualOverheadEditor.js";
import { OverheadAccountReview } from "../components/OverheadAccountReview.js";
import { AccountingExpenseEvidence } from "../components/AccountingExpenseEvidence.js";
import { useApiClient } from "../lib/api-context.js";
import type { ExpenseAllocation, ExpenseClassification, OverheadReviewView } from "../lib/overhead-review.js";
import { useToast } from "../lib/toast.js";
import { useViewportWidth } from "../lib/use-viewport.js";
import { relockFinancialQueries } from "../lib/query-invalidation.js";

export default function OverheadReviewPage(): React.ReactElement {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedReturn = searchParams.get("returnTo");
  const returnTo = requestedReturn?.startsWith("/") && !requestedReturn.startsWith("//")
    ? requestedReturn
    : "/costs";
  const returnLabel = returnTo === "/welcome" ? "Back to setup" : "Back to Costs";
  const compact = useViewportWidth() < 820;

  const review = useQuery({
    queryKey: ["overhead-review"],
    queryFn: () => api.get<OverheadReviewView>("/business-costs/overhead/review"),
  });
  const account = useQuery({
    queryKey: ["account"],
    queryFn: () => api.get<{ account: { baseCurrency: string }; role?: string }>("/account"),
  });

  const refresh = (): void => {
    relockFinancialQueries(queryClient);
    void queryClient.invalidateQueries({ queryKey: ["overhead-review"] });
    // Any review edit invalidates the last approved accounting total on the
    // server, so every profit-facing cache must stop using it immediately.
    void queryClient.invalidateQueries({ queryKey: ["overhead"] });
    void queryClient.invalidateQueries({ queryKey: ["overhead-history"] });
    void queryClient.invalidateQueries({ queryKey: ["data-trust-warnings"] });
  };
  const updateAccount = useMutation({
    mutationFn: (input: { accountId: string; classification: ExpenseClassification | null; planningMonthlyAmount?: number | null }) =>
      api.patch("/business-costs/overhead/review/accounts/" + input.accountId, {
        classification: input.classification,
        ...(input.planningMonthlyAmount !== undefined ? { planningMonthlyAmount: input.planningMonthlyAmount } : {}),
      }),
    onSuccess: refresh,
    onError: () => toast.error("We couldn't save that answer. Try again."),
  });
  const saveSplit = useMutation({
    mutationFn: (input: { amountId: string; allocations: ExpenseAllocation[] }) =>
      api.put("/business-costs/overhead/review/amounts/" + input.amountId + "/split", { allocations: input.allocations }),
    onSuccess: () => {
      refresh();
      toast.success("Split saved. The parts still match QuickBooks.");
    },
    onError: () => toast.error("That split does not match the QuickBooks amount."),
  });
  const saveManual = useMutation({
    mutationFn: (draft: ManualOverheadDraft) => draft.id
      ? api.put("/business-costs/overhead/review/manual/" + draft.id, draft)
      : api.post("/business-costs/overhead/review/manual", draft),
    onSuccess: (_data, draft) => {
      refresh();
      toast.success(draft.id ? "Manual cost updated." : "Manual cost added.");
    },
    onError: () => toast.error("We couldn't save that manual cost. Check the details and try again."),
  });
  const removeManual = useMutation({
    mutationFn: (id: string) => api.delete("/business-costs/overhead/review/manual/" + id),
    onSuccess: () => {
      refresh();
      toast.success("Manual cost removed.");
    },
    onError: () => toast.error("We couldn't remove that cost. Try again."),
  });
  const confirm = useMutation({
    mutationFn: () => api.post<{ monthlyOverheadAmount: number }>("/business-costs/overhead/review/confirm", {}),
    onSuccess: (data) => {
      refresh();
      toast.success(`${money(data.monthlyOverheadAmount, currency)} in normal monthly overhead is ready to use.`);
      if (requestedReturn) navigate(returnAfterReview(returnTo));
    },
    onError: () => toast.error("Finish every expense review and fix any source mismatch first."),
  });

  const data = review.data;
  const currency = data?.source?.currency ?? account.data?.account.baseCurrency ?? "USD";
  const canEdit = account.data?.role === "owner" || account.data?.role === "admin";
  const busy = updateAccount.isPending || saveSplit.isPending || saveManual.isPending || removeManual.isPending;

  if (review.isLoading) {
    return (
      <div style={{ maxWidth: 1180 }}>
        <button type="button" onClick={() => navigate(returnTo)} style={styles.backButton}><ArrowLeft size={15} /> {returnLabel}</button>
        <div style={styles.loadingCard}>Loading your accounting expenses...</div>
      </div>
    );
  }

  if (review.isError || !data) {
    return (
      <div style={{ maxWidth: 1180 }}>
        <button type="button" onClick={() => navigate(returnTo)} style={styles.backButton}><ArrowLeft size={15} /> {returnLabel}</button>
        <div style={styles.errorCard}>
          <AlertCircle size={18} />
          <div><strong>We couldn't load your overhead review.</strong><div style={styles.smallCopy}>Refresh the page. Your saved answers are still there.</div></div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1180, paddingBottom: 96 }}>
      <button type="button" onClick={() => navigate(returnTo)} style={styles.backButton}><ArrowLeft size={15} /> {returnLabel}</button>
      <div style={{ marginTop: 14 }}>
        <h1 style={{ margin: 0, fontSize: compact ? 21 : 24 }}>Check your business overhead</h1>
        <p style={styles.pageDescription}>
          We show what came from your books. You decide what repeats, what happened once, and what is already counted somewhere else.
        </p>
      </div>

      {data.source ? (
        <section style={styles.sourceCard(data.source.totalsMatch)}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
            {data.source.totalsMatch
              ? <CheckCircle2 size={18} style={{ color: "var(--good)", flexShrink: 0, marginTop: 1 }} />
              : <AlertCircle size={18} style={{ color: "var(--bad)", flexShrink: 0, marginTop: 1 }} />}
            <div>
              <div style={{ fontSize: 13.5, fontWeight: 750 }}>
                {providerName(data.source.platform)} {data.source.totalsMatch ? "totals match" : "totals need attention"}
              </div>
              <div style={styles.smallCopy}>
                {data.source.reportMonthCount} complete months, {dateLabel(data.source.periodStart)} to {dateLabel(data.source.periodEnd)}. Last refreshed {new Date(data.source.fetchedAt).toLocaleString()}.
              </div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, color: "var(--faint)", fontSize: 11.5 }}>
            <Link2 size={14} /> Source amounts stay locked to your accounting report
          </div>
        </section>
      ) : (
        <section style={styles.emptySource}>
          <AlertCircle size={19} style={{ color: "var(--c2)", flexShrink: 0 }} />
          <div>
            <div style={{ fontWeight: 750 }}>No account-level expense report is ready yet.</div>
            <div style={styles.smallCopy}>Connect QuickBooks and complete a refresh. Then this page will list each expense for review.</div>
            <button type="button" onClick={() => navigate("/connections")} style={{ ...styles.secondaryButton, marginTop: 10 }}>Go to Connections</button>
          </div>
        </section>
      )}

      <AccountingExpenseEvidence canEdit={canEdit} />

      <div style={{ display: "grid", gridTemplateColumns: compact ? "1fr" : "repeat(2, minmax(0, 1fr))", gap: 12, marginTop: 16 }}>
        <div style={styles.summaryCard}>
          <div style={styles.summaryLabel}>Normal monthly overhead</div>
          <div className="dd-num" style={styles.summaryValue}>{money(data.summary.normalMonthlyOverhead, currency)}</div>
          <div style={styles.smallCopy}>Used for planning after you confirm the review.</div>
        </div>
        <div style={styles.summaryCard}>
          <div style={styles.summaryLabel}>One-time expenses in this review</div>
          <div className="dd-num" style={styles.summaryValue}>{money(data.summary.oneTimeExpensesInReviewPeriod, currency)}</div>
          <div style={styles.smallCopy}>Kept in the month they happened. Not added to normal monthly overhead.</div>
        </div>
      </div>

      {data.accounts.length > 0 && (
        <section style={{ marginTop: 24 }}>
          <h2 style={styles.sectionTitle}>Expenses from {providerName(data.source?.platform ?? "accounting")}</h2>
          <p style={styles.sectionDescription}>
            Review our suggestion, then choose your answer. For regular overhead, you can change the monthly planning amount without changing the source.
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {data.accounts.map((row) => (
              <OverheadAccountReview
                key={row.id}
                account={row}
                currency={currency}
                compact={compact}
                canEdit={canEdit}
                saving={busy}
                onUpdateAccount={async (input) => {
                  try { await updateAccount.mutateAsync(input); } catch { /* toast is handled by the mutation */ }
                }}
                onSaveSplit={async (input) => {
                  try { await saveSplit.mutateAsync(input); return true; } catch { return false; }
                }}
              />
            ))}
          </div>
        </section>
      )}

      <div style={{ marginTop: 20 }}>
        <ManualOverheadEditor
          items={data.manualItems}
          currency={currency}
          compact={compact}
          canEdit={canEdit}
          saving={busy}
          onSave={async (draft) => {
            try { await saveManual.mutateAsync(draft); return true; } catch { return false; }
          }}
          onRemove={async (id) => {
            try { await removeManual.mutateAsync(id); return true; } catch { return false; }
          }}
        />
      </div>

      <div style={styles.confirmBar(compact)}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 8, minWidth: 0 }}>
          {data.summary.approved
            ? <CheckCircle2 size={18} style={{ color: "var(--good)", flexShrink: 0, marginTop: 1 }} />
            : <AlertCircle size={18} style={{ color: data.summary.readyToUse ? "var(--c2)" : "var(--bad)", flexShrink: 0, marginTop: 1 }} />}
          <div>
            <div style={{ fontSize: 12.5, fontWeight: 700 }}>
              {data.summary.approved
                ? "These overhead numbers are approved and in use."
                : data.summary.readyToUse
                  ? "Your changes are saved. Confirm them before profit uses them."
                  : reviewStatus(data)}
            </div>
            <div style={styles.smallCopy}>
              {data.summary.approved
                ? "Any change will pause these numbers until you confirm again."
                : data.summary.readyToUse
                  ? "Profit stays unverified until you confirm this review."
                  : "No imported overhead will be used until this review is complete."}
            </div>
          </div>
        </div>
        {canEdit && !data.summary.approved && (
          <button
            type="button"
            disabled={!data.summary.readyToUse || confirm.isPending}
            onClick={() => confirm.mutate()}
            style={styles.confirmButton}
          >
            {confirm.isPending ? "Saving..." : "Confirm and use these numbers"}
          </button>
        )}
      </div>
    </div>
  );
}

function reviewStatus(data: OverheadReviewView): string {
  const parts: string[] = [];
  if (!data.source) parts.push("Connect your accounting report");
  if (data.source && !data.source.totalsMatch) parts.push("Fix the source mismatch");
  if (data.summary.unreviewedAccountCount > 0) parts.push(`${data.summary.unreviewedAccountCount} account${data.summary.unreviewedAccountCount === 1 ? " needs" : "s need"} an answer`);
  if (data.summary.invalidSplitCount > 0) parts.push(`${data.summary.invalidSplitCount} split${data.summary.invalidSplitCount === 1 ? " does" : "s do"} not match`);
  return parts.join(". ") + ".";
}

function providerName(platform: string): string {
  if (platform === "quickbooks") return "QuickBooks";
  if (platform === "xero") return "Xero";
  if (platform === "freshbooks") return "FreshBooks";
  return "Accounting report";
}

function dateLabel(value: string): string {
  return new Date(`${value.slice(0, 10)}T12:00:00`).toLocaleDateString("en-US", { month: "short", year: "numeric" });
}

function money(value: number, currency: string): string {
  return new Intl.NumberFormat("en-US", { style: "currency", currency, maximumFractionDigits: 0 }).format(value);
}

/** Tell the saved costs conversation to move past its old overhead question.
 * Other destinations keep their original URL untouched. */
export function returnAfterReview(returnTo: string): string {
  if (!returnTo.startsWith("/onboarding/chat")) return returnTo;
  const [path, rawQuery = ""] = returnTo.split("?", 2);
  const params = new URLSearchParams(rawQuery);
  params.set("overheadReviewed", "1");
  return `${path}?${params.toString()}`;
}

const styles = {
  backButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    padding: 0,
    border: "none",
    background: "transparent",
    color: "var(--accent)",
    cursor: "pointer",
    font: "inherit",
    fontSize: 12.5,
    fontWeight: 650,
  } as React.CSSProperties,
  pageDescription: { margin: "5px 0 0", color: "var(--body)", fontSize: 13, lineHeight: 1.55, maxWidth: 760 } as React.CSSProperties,
  sourceCard: (good: boolean): React.CSSProperties => ({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 12,
    marginTop: 18,
    padding: "13px 15px",
    background: "var(--surface-2)",
    border: `1px solid ${good ? "color-mix(in srgb, var(--good) 35%, var(--border))" : "color-mix(in srgb, var(--bad) 45%, var(--border))"}`,
    borderRadius: 9,
  }),
  emptySource: {
    display: "flex",
    gap: 10,
    marginTop: 18,
    padding: 16,
    background: "var(--surface-2)",
    border: "1px solid var(--border)",
    borderRadius: 9,
  } as React.CSSProperties,
  summaryCard: { padding: 16, background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 10 } as React.CSSProperties,
  summaryLabel: { color: "var(--body)", fontSize: 12.5, fontWeight: 650 } as React.CSSProperties,
  summaryValue: { marginTop: 5, fontSize: 25, fontWeight: 780, letterSpacing: "-0.02em" } as React.CSSProperties,
  smallCopy: { color: "var(--faint)", fontSize: 11.5, lineHeight: 1.45, marginTop: 3 } as React.CSSProperties,
  sectionTitle: { margin: 0, fontSize: 16, fontWeight: 750 } as React.CSSProperties,
  sectionDescription: { margin: "4px 0 12px", color: "var(--body)", fontSize: 12.5, lineHeight: 1.5, maxWidth: 760 } as React.CSSProperties,
  confirmBar: (compact: boolean): React.CSSProperties => ({
    position: "sticky",
    bottom: 14,
    marginTop: 20,
    display: "flex",
    flexDirection: compact ? "column" : "row",
    alignItems: compact ? "stretch" : "center",
    justifyContent: "space-between",
    gap: 12,
    padding: "12px 14px",
    background: "color-mix(in srgb, var(--surface) 94%, transparent)",
    border: "1px solid var(--border)",
    borderRadius: 10,
    boxShadow: "0 8px 24px color-mix(in srgb, var(--text) 12%, transparent)",
    backdropFilter: "blur(10px)",
    zIndex: 12,
  }),
  confirmButton: {
    border: "none",
    borderRadius: 8,
    padding: "10px 15px",
    background: "var(--accent)",
    color: "var(--accent-contrast)",
    cursor: "pointer",
    font: "inherit",
    fontSize: 13,
    fontWeight: 750,
    whiteSpace: "nowrap",
  } as React.CSSProperties,
  secondaryButton: {
    border: "1px solid var(--border)",
    borderRadius: 7,
    padding: "7px 11px",
    background: "transparent",
    color: "var(--body)",
    cursor: "pointer",
    font: "inherit",
    fontSize: 12.5,
    fontWeight: 650,
  } as React.CSSProperties,
  loadingCard: { marginTop: 18, padding: 18, border: "1px solid var(--border)", borderRadius: 10, color: "var(--body)", fontSize: 13 } as React.CSSProperties,
  errorCard: { display: "flex", gap: 10, marginTop: 18, padding: 18, border: "1px solid color-mix(in srgb, var(--bad) 45%, var(--border))", borderRadius: 10, color: "var(--bad)" } as React.CSSProperties,
};
